import { useState, useEffect } from 'react';
import { AdminTenant, AdminTenantUpdate, adminService } from '../services/admin-service';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { X } from 'lucide-react';

interface AdminEditTenantModalProps {
  tenant: AdminTenant | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AdminEditTenantModal({ tenant, isOpen, onClose, onSuccess }: AdminEditTenantModalProps) {
  const [formData, setFormData] = useState<AdminTenantUpdate>({
    name: '',
    domain: '',
    plan: 'free',
    status: 'active',
    account_type: 'personal',
    max_users: 1
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name,
        domain: tenant.domain,
        plan: tenant.plan,
        status: tenant.status,
        account_type: tenant.account_type,
        max_users: tenant.max_users || 1
      });
    }
  }, [tenant]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenant) return;

    setIsLoading(true);
    try {
      await adminService.updateTenant(tenant.id, formData);
      toast.success('Tenant atualizado com sucesso!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao atualizar tenant:', error);
      toast.error('Erro ao atualizar tenant. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!tenant) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Editar Tenant</DialogTitle>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Básicas */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">
              Informações Básicas
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nome da Empresa *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Nome da empresa"
                />
              </div>

              <div>
                <Label htmlFor="domain">Domínio *</Label>
                <Input
                  id="domain"
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  required
                  placeholder="empresa-slug"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Usado para identificação única
                </p>
              </div>
            </div>
          </div>

          {/* Plano e Status */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">
              Plano e Status
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="plan">Plano *</Label>
                <Select
                  value={formData.plan}
                  onValueChange={(value: 'free' | 'premium') => 
                    setFormData({ ...formData, plan: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: 'active' | 'inactive' | 'suspended' | 'cancelled') => 
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                    <SelectItem value="suspended">Suspenso</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="account_type">Tipo de Conta *</Label>
                <Select
                  value={formData.account_type}
                  onValueChange={(value: 'personal' | 'business' | 'enterprise') => 
                    setFormData({ ...formData, account_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal">Pessoal</SelectItem>
                    <SelectItem value="business">Empresa</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Limites */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">
              Limites e Configurações
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="max_users">Máximo de Usuários</Label>
                <Input
                  id="max_users"
                  type="number"
                  min="1"
                  value={formData.max_users}
                  onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                  placeholder="Ex: 10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Deixe em branco para ilimitado (apenas Premium)
                </p>
              </div>
            </div>
          </div>

          {/* Informações do Tenant */}
          <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900">
              Informações do Tenant
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">ID Exibição:</span>
                <p className="font-mono text-lg font-bold text-blue-600">
                  #{tenant.display_id || '---'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">UUID:</span>
                <p className="font-mono text-xs text-gray-700 break-all">{tenant.id}</p>
              </div>
              <div>
                <span className="text-gray-500">Usuários Ativos:</span>
                <p className="font-semibold text-gray-900">{tenant.user_count || 0}</p>
              </div>
              <div>
                <span className="text-gray-500">Total de Mensagens:</span>
                <p className="font-semibold text-gray-900">{tenant.total_messages || 0}</p>
              </div>
              <div>
                <span className="text-gray-500">Criado em:</span>
                <p className="text-gray-700">
                  {new Date(tenant.created_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isLoading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

